import { LitElement, html } from "lit";
import { customElement, state } from "lit/decorators.js";

import "@material/web/textfield/outlined-text-field.js";
import "@material/web/button/filled-button.js";

import { pb } from "../lib/pb";
import { navigate } from "../lib/navigation";
import { consumeReturnTo } from "../router";
import { inputValue, errorMessage } from "../lib/dom";
import { sharedStyles, authStyles } from "../styles/shared";

@customElement("signup-page")
export class SignupPage extends LitElement {
  static styles = [sharedStyles, authStyles];

  @state() private displayName = "";
  @state() private email = "";
  @state() private password = "";
  @state() private passwordConfirm = "";
  @state() private loading = false;
  @state() private error = "";

  private async handleSubmit(e: Event): Promise<void> {
    e.preventDefault();
    this.error = "";

    if (this.password !== this.passwordConfirm) {
      this.error = "Passwords do not match.";
      return;
    }

    this.loading = true;
    try {
      await pb.collection("tenancy_users").create({
        email: this.email,
        password: this.password,
        passwordConfirm: this.passwordConfirm,
        display_name: this.displayName,
      });
      await pb
        .collection("tenancy_users")
        .authWithPassword(this.email, this.password);
      // Same restore as login: an invitee who signed up to accept an emailed
      // link must land back on that link, query string and all.
      const returnTo = consumeReturnTo();
      if (returnTo) {
        window.location.assign(returnTo);
        return;
      }
      await navigate("/onboarding");
    } catch (err) {
      this.error = errorMessage(err, "Could not create your account.");
    } finally {
      this.loading = false;
    }
  }

  render() {
    return html`
      <div class="auth-page">
        <div class="auth-card">
          <h1>Create your account</h1>
          <p class="subtitle">
            Start authoring Digital Product Passports for your products.
          </p>
          <form @submit=${(e: Event) => void this.handleSubmit(e)}>
            <md-outlined-text-field
              label="Your name"
              autocomplete="name"
              required
              .value=${this.displayName}
              @input=${(e: Event) => (this.displayName = inputValue(e))}
            ></md-outlined-text-field>
            <md-outlined-text-field
              label="Email"
              type="email"
              autocomplete="email"
              required
              .value=${this.email}
              @input=${(e: Event) => (this.email = inputValue(e))}
            ></md-outlined-text-field>
            <md-outlined-text-field
              label="Password"
              type="password"
              autocomplete="new-password"
              required
              minlength="8"
              .value=${this.password}
              @input=${(e: Event) => (this.password = inputValue(e))}
            ></md-outlined-text-field>
            <md-outlined-text-field
              label="Confirm password"
              type="password"
              autocomplete="new-password"
              required
              minlength="8"
              .value=${this.passwordConfirm}
              @input=${(e: Event) => (this.passwordConfirm = inputValue(e))}
            ></md-outlined-text-field>
            ${this.error
              ? html`<p class="error-text">${this.error}</p>`
              : null}
            <md-filled-button type="submit" ?disabled=${this.loading}>
              ${this.loading ? "Creating account…" : "Create account"}
            </md-filled-button>
          </form>
          <p class="footer-link">
            Already have an account? <a href="/login">Sign in</a>
          </p>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "signup-page": SignupPage;
  }
}
